import React, { useMemo } from "react";
import FAQCard from "../../components/FAQCard/FAQCard";
import { InlineLoadingIcon } from "../../components/InlineLoadingIcon/InlineLoadingIcon";
import { TagCountList } from "../../components/TagCountList/TagCountList";
import { TagList } from "../../components/TagList/TagList";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { useInvestorDatabaseMetadata } from "../../hooks/use-investor-database-metadata";
import { mapInvestorRoleToText } from "../../lib/map-investor-role-to-text";
import { InvestorRole } from "../../types/ImportedInvestorEntry";
import "./VCDirectoryHomePage.scss";

const VCDirectoryHomePage: React.FC = () => {
  useDocumentTitle("VC Database");
  const {
    investorCount,
    investorCountsByRole,
    hasInvestorTypeCounts,
    vcDirectoryMetadata,
    isLoading,
  } = useInvestorDatabaseMetadata();

  const sectorCounts = useMemo(() => {
    const counts: Map<string, number> = new Map();
    if (vcDirectoryMetadata?.sectorFocusWeighted) {
      for (const tagCountInfo of vcDirectoryMetadata.sectorFocusWeighted) {
        if (tagCountInfo.count > 1) {
          counts.set(tagCountInfo.tagKey, tagCountInfo.count);
        }
      }
    }
    return counts;
  }, [vcDirectoryMetadata]);

  return (
    <div className="vc-directory-home-page">
      <div className="page-header">
        Find VCs
        <div className="page-subheader">
          Is it raisin' time? Let's find the right VCs for your startup. The
          directory currently contains{" "}
          <strong>{Number(investorCount).toLocaleString()}</strong> VCs, Angels,
          Firms, etc.
        </div>
      </div>
      <div className="row">
        <div className="col-lg-8">
          {isLoading && <InlineLoadingIcon />}
          <div>
            {investorCountsByRole && (
              <>
                {hasInvestorTypeCounts && (
                  <div className="tag-list-container">
                    <div className="tag-list-header">Investor Types</div>
                    <TagCountList<InvestorRole>
                      className="investor-count-by-role"
                      counts={investorCountsByRole}
                      thingRoute={`/investors/search?role=$VALUE`}
                      mapThingToText={mapInvestorRoleToText}
                    />
                  </div>
                )}
                <div className="tag-list-container">
                  <div className="tag-list-header">Stage Focus</div>
                  <TagList<string>
                    tags={vcDirectoryMetadata?.stageFocus ?? []}
                    sort
                    thingRoute={`/investors/search?stageFocus=$VALUE`}
                  />
                </div>
                <div className="tag-list-container">
                  <div className="tag-list-header">Sector Focus</div>
                  <TagCountList<string>
                    counts={sectorCounts}
                    sort="alphabetical"
                    thingRoute={`/investors/search?sectorFocus=$VALUE`}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="col-lg-4 sidebar">
          <div className="inner">
            <div className="faq-cards">
              <FAQCard header="Where does the data come from?">
                <div>
                  Data is sourced from a number of <strong>Airtable</strong> and{" "}
                  <strong>Folk</strong> contact databases passed around by
                  industry insiders. We also source data from{" "}
                  <strong>LinkedIn</strong> and other publicly available
                  datasets.
                </div>
              </FAQCard>
              <FAQCard header="How do I use the data?">
                <div>
                  You can use the data to find VCs and Angels for your startup.
                </div>
              </FAQCard>
              <FAQCard header="I noticed some incorrect or duplicate data...">
                <div>
                  That's great! Please{" "}
                  <a href="mailto:vcdb@georgemike.com">email us</a> and we'll
                  get it fixed. We're working on automating data-quality
                  reporting and management.
                </div>
              </FAQCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VCDirectoryHomePage;
